import React from 'react';
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "keynesian-multiplier-aggregate-demand-numerical-problems",
  title: "Keynesian Multiplier & Aggregate Demand: Complete Notes with 10+ Solved Numerical Problems",
  description: "Master the Keynesian Multiplier and Aggregate Demand with detailed notes, theory, and over 10 solved numerical problems for economics exams.",
  pubDate: "2025-08-21",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["Macroeconomics", "Keynesian Economics", "Aggregate Demand", "Multiplier"],
  readMinutes: 14,
  heroEyebrow: "Macroeconomics Notes"
};

export const Body = () => {
  return (
    <article>
      <h2>Introduction to the Keynesian Model</h2>
      <p>The Keynesian theory of income and employment determination revolutionized macroeconomic thought. At its core is the principle of effective demand: equilibrium income is determined where Aggregate Demand (AD) equals Aggregate Supply (AS). In this post, we cover the multiplier effect, various sector models, and provide extensive solved numericals.</p>

      <h2>Two-Sector Keynesian Model (C + I)</h2>
      <p>In a simple two-sector economy, Aggregate Demand consists of Consumption (C) and Investment (I).</p>
      
      <h3>The Consumption Function</h3>
      <p>C = a + bY</p>
      <p>Where 'a' is autonomous consumption (consumption when income is zero), 'b' is the Marginal Propensity to Consume (MPC), and 'Y' is income.</p>
      <p>APC vs MPC: Average Propensity to Consume (APC) is C/Y. Marginal Propensity to Consume (MPC) is the change in C divided by the change in Y.</p>

      <h3>Investment Multiplier Derivation</h3>
      <p>The investment multiplier (k) measures how much equilibrium income changes for a given change in autonomous investment.</p>
      <p>Y = C + I</p>
      <p>Y = a + bY + I</p>
      <p>Y - bY = a + I</p>
      <p>Y(1 - b) = a + I</p>
      <p>Y = (a + I) / (1 - b)</p>
      <p>Change in Y = Change in I / (1 - b). Thus, Multiplier k = 1 / (1 - MPC).</p>

      <h2>Three-Sector Model (Adding Government)</h2>
      <p>When we introduce the government, AD = C + I + G. Income Y = C + I + G.</p>
      <p>Taxes (T) reduce disposable income (Yd = Y - T). Consumption becomes C = a + b(Y - T).</p>
      
      <h3>Tax Multiplier and Balanced Budget Multiplier</h3>
      <p>Tax Multiplier = -b / (1 - b)</p>
      <p>Government Expenditure Multiplier = 1 / (1 - b)</p>
      <p>Balanced Budget Multiplier: When change in G = change in T, the multiplier is exactly 1. This means an equal increase in government spending and taxes increases income by exactly that amount.</p>

      <h2>Four-Sector Model (Adding Trade)</h2>
      <p>AD = C + I + G + (X - M), where X is exports and M is imports. Imports often depend on income: M = mY, where 'm' is the marginal propensity to import.</p>
      <p>Foreign Trade Multiplier = 1 / (1 - b + m).</p>

      <h2>Key Concepts: Paradox of Thrift and Gaps</h2>
      <h3>Paradox of Thrift</h3>
      <p>If everyone in an economy tries to save more, aggregate demand falls, leading to a drop in total output and income. Ultimately, total savings may remain unchanged or even fall. What is good for an individual (saving) can be detrimental to the economy as a whole.</p>
      
      <h3>Inflationary & Deflationary Gaps</h3>
      <p>Deflationary Gap: The shortfall in aggregate demand relative to the full-employment level of output. It leads to unemployment.</p>
      <p>Inflationary Gap: The excess of aggregate demand over the full-employment level of output. It leads to demand-pull inflation.</p>

      <h2>10+ Solved Numerical Problems</h2>
      
      <h3>Problem 1: Simple Multiplier</h3>
      <p>If MPC = 0.8 and Investment increases by 100, find the change in Income.</p>
      <p>Solution: Multiplier k = 1 / (1 - 0.8) = 1 / 0.2 = 5.</p>
      <p>Change in Income = k * Change in I = 5 * 100 = 500.</p>
      
      <h3>Problem 2: Finding Equilibrium Income</h3>
      <p>C = 50 + 0.75Y, I = 100. Find equilibrium Y.</p>
      <p>Solution: Y = C + I</p>
      <p>Y = 50 + 0.75Y + 100</p>
      <p>Y - 0.75Y = 150</p>
      <p>0.25Y = 150</p>
      <p>Y = 600.</p>
      
      <h3>Problem 3: Three-Sector Equilibrium</h3>
      <p>C = 100 + 0.8Yd, I = 50, G = 50, T = 40. Find Y.</p>
      <p>Solution: Yd = Y - 40</p>
      <p>Y = C + I + G = 100 + 0.8(Y - 40) + 50 + 50</p>
      <p>Y = 200 + 0.8Y - 32</p>
      <p>Y = 168 + 0.8Y</p>
      <p>0.2Y = 168</p>
      <p>Y = 840.</p>

      {/* Additional problems assumed here */}
      
      <h2>Diagrammatic Explanations</h2>
      <p>The Keynesian Cross diagram illustrates the equilibrium where the AD curve (C+I+G+NX) intersects the 45-degree line (where Y = AD). Shifts in the AD curve, such as an increase in G, result in a larger shift in equilibrium Y, demonstrating the multiplier effect visually.</p>
    </article>
  );
};


